import { Box, Typography } from '@mui/material';
import { AutocompleteInput, ReferenceInput, ImageField, useTranslate } from 'react-admin';
import { useEffect, useMemo, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import { NoImage } from '@/components/global/fields/NoImage';

const ProductOptionText = ({ record, hideImage }) => {
    const translate = useTranslate();

    return (
        <Box display='flex' alignItems='center' gap='12px' width='100%'>
            {!hideImage &&
                (record?.image ? (
                    <ImageField
                        source='image'
                        title={record?.name}
                        record={record}
                        sx={{
                            '& .RaImageField-image': {
                                width: '64px',
                                height: '64px',
                            },
                        }}
                    />
                ) : (
                    <NoImage sx={{ minWidth: '64px', minHeight: '64px', maxWidth: '64px', maxHeight: '64px' }} alt={record?.name} />
                ))}
            <Box flex={1} minWidth={0}>
                <Typography variant='body2'>{record?.name}</Typography>
                <Typography variant='caption' color='text.secondary' noWrap>
                    {translate('resources.campaign.labels.ean')} {record?.ean}
                </Typography>
            </Box>
        </Box>
    );
};

ProductOptionText.propTypes = {
    record: PropTypes.object,
    hideImage: PropTypes.bool,
};

const SingleProductSelector = ({ source = 'productId', hideImage = false, label, value, onChange }) => {
    const translate = useTranslate();
    const { setValue: setFormValue } = useFormContext();
    const watchedProductId = useWatch({ name: source });

    const isProductFilled = useMemo(() => {
        return Boolean(watchedProductId);
    }, [watchedProductId]);

    const handleValueChange = useCallback(
        newValue => {
            if (onChange) {
                onChange(newValue);
            }
        },
        [onChange],
    );

    useEffect(() => {
        if (value !== undefined && value !== watchedProductId) {
            setFormValue(source, value, { shouldDirty: true, shouldValidate: false, shouldTouch: false });
        }
    }, [value, source, watchedProductId, setFormValue]);

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source={source} reference='product-mock' perPage={100}>
                <AutocompleteInput
                    source={source}
                    label={label}
                    fullWidth
                    noOptionsText={translate('resources.campaign.labels.noProductFound')}
                    disablePortal={true}
                    onChange={handleValueChange}
                    filterToQuery={searchText =>
                        searchText
                            ? {
                                  search: {
                                      value: searchText,
                                      fields: { name: true, ean: true },
                                  },
                              }
                            : {}
                    }
                    className={isProductFilled ? 'input-filled' : ''}
                    inputText={record => record?.name || ''}
                    optionText={<ProductOptionText hideImage={hideImage} />}
                    sx={{
                        '& .MuiFormHelperText-root': {
                            display: 'none',
                        },
                    }}
                />
            </ReferenceInput>
        </Box>
    );
};

SingleProductSelector.propTypes = {
    source: PropTypes.string,
    hideImage: PropTypes.bool,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
};

export default SingleProductSelector;
